import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, TrendingUp, DollarSign, ShoppingCart, Database } from "lucide-react";
import { toast } from "sonner";
import api from "./api";

/**
 * Admin → Reports → Monthly breakdown.
 *
 * Shows orders / revenue / paid-revenue / estimated profit per month.
 * Profit = paid_revenue × (1 − HERITAGE_COGS_RATIO). Tweak via env var.
 */
export default function ConsoleReports() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(12);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/admin/reports/monthly?months=${months}`);
        setData(r.data);
      } catch (e) {
        if (e?.response?.status === 401) navigate("/admin/login");
        else setErr(e?.response?.data?.detail || "Failed to load");
      }
    })();
  }, [months, navigate]);

  if (err) return <p data-testid="reports-error" className="text-[var(--dahlias-rust)]">{err}</p>;
  if (!data) return <p className="text-[var(--dahlias-ink)]/60">Loading…</p>;

  const backfillNow = async () => {
    if (!window.confirm("Scan historical orders and create catalogue varieties for any new names? Existing varieties won't be touched.")) return;
    try {
      const r = await api.post("/admin/inventory/backfill-from-orders");
      toast.success(`Scanned ${r.data.scanned_orders} orders, created ${r.data.created_varieties} new varieties (unpublished).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed.");
    }
  };

  const maxRevenue = Math.max(1, ...data.rows.map((r) => r.revenue));
  const cogsPct = Math.round(data.cogs_ratio * 100);

  return (
    <div data-testid="admin-reports" className="space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="font-label tracking-[0.4em] text-[11px] uppercase text-[#6E1E3A]">✦ Reports ✦</span>
          <h1 className="mt-2 font-display text-4xl text-[var(--dahlias-forest)] leading-[1.05]">
            Monthly <span className="font-display-italic text-[#6E1E3A]">breakdown.</span>
          </h1>
          <p className="mt-2 text-sm text-[var(--dahlias-ink)]/70 max-w-xl">
            Orders, revenue and an estimated profit per calendar month. Profit assumes a {cogsPct}% cost-of-goods ratio, set the <code>HERITAGE_COGS_RATIO</code> env variable to sharpen it.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm">
          <span className="font-label tracking-[0.25em] text-[10px] uppercase text-[var(--dahlias-sage-deep)]">Window</span>
          <select
            value={months}
            onChange={(e) => setMonths(parseInt(e.target.value, 10))}
            data-testid="reports-months-select"
            className="h-9 px-3 wobble-pill bg-white border border-[var(--dahlias-sage)]/30 text-sm"
          >
            <option value={6}>Last 6 months</option>
            <option value={12}>Last 12 months</option>
            <option value={24}>Last 24 months</option>
            <option value={36}>Last 36 months</option>
          </select>
        </label>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard icon={<ShoppingCart className="w-5 h-5" />} label="Orders" value={data.total_orders} />
        <SummaryCard icon={<DollarSign className="w-5 h-5" />} label="Revenue" value={`NZ$${data.total_revenue.toFixed(2)}`} />
        <SummaryCard icon={<DollarSign className="w-5 h-5" />} label="Paid revenue" value={`NZ$${data.total_paid_revenue.toFixed(2)}`} />
        <SummaryCard icon={<TrendingUp className="w-5 h-5" />} label={`Profit (≈${100 - cogsPct}%)`} value={`NZ$${data.total_profit.toFixed(2)}`} highlight />
      </div>

      <section className="bg-[#FBE6D8] border-2 border-[#6E1E3A]/25 wobble-a p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <span className="font-label tracking-[0.4em] text-[10px] uppercase text-[#6E1E3A]">✦ Inventory backfill ✦</span>
          <h2 className="mt-1 font-display text-xl text-[var(--dahlias-forest)]">Match historical orders to the catalogue</h2>
          <p className="text-xs text-[var(--dahlias-ink)]/70 mt-1 max-w-2xl">
            Scan all imported orders and create unpublished catalogue entries for any variety name we haven&apos;t catalogued yet. Review and publish them in the Catalogue admin.
          </p>
        </div>
        <button
          type="button"
          onClick={backfillNow}
          data-testid="reports-backfill-btn"
          className="inline-flex items-center gap-1.5 h-10 px-4 wobble-pill bg-[#6E1E3A] hover:bg-[#8a2548] text-[#FBE6D8] text-xs font-label tracking-[0.3em] uppercase shadow"
        >
          <Database className="w-3.5 h-3.5" /> Run backfill
        </button>
      </section>

      <section className="bg-white border-2 border-[#6E1E3A]/15 wobble-a p-5">
        <h2 className="font-display text-2xl text-[var(--dahlias-forest)] flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-[#6E1E3A]" /> Month-by-month
        </h2>
        {data.rows.length === 0 ? (
          <p className="mt-4 text-sm text-[var(--dahlias-ink)]/60">No order history yet for the selected window.</p>
        ) : (
          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-sm" data-testid="reports-monthly-table">
              <thead>
                <tr className="text-left text-[10px] font-label tracking-[0.3em] uppercase text-[#6E1E3A]/85 border-b border-[#6E1E3A]/20">
                  <th className="py-2 pr-3">Month</th>
                  <th className="py-2 pr-3 text-right">Orders</th>
                  <th className="py-2 pr-3 text-right">Revenue</th>
                  <th className="py-2 pr-3 text-right">Paid</th>
                  <th className="py-2 pr-3 text-right">Profit</th>
                  <th className="py-2 pl-3 w-1/3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#6E1E3A]/10">
                {data.rows.map((r) => (
                  <tr key={r.month} data-testid={`reports-row-${r.month}`}>
                    <td className="py-2 pr-3 font-medium">
                      {monthLabel(r.month)}
                    </td>
                    <td className="py-2 pr-3 text-right">{r.orders}</td>
                    <td className="py-2 pr-3 text-right">${r.revenue.toFixed(2)}</td>
                    <td className="py-2 pr-3 text-right text-[var(--dahlias-sage-deep)]">${r.paid_revenue.toFixed(2)}</td>
                    <td className="py-2 pr-3 text-right font-display text-base text-[#6E1E3A]">${r.profit.toFixed(2)}</td>
                    <td className="py-2 pl-3">
                      <div className="h-3 bg-[#6E1E3A]/10 wobble-pill overflow-hidden">
                        <div
                          className="h-full bg-[#6E1E3A]/80"
                          style={{ width: `${Math.round((r.revenue / maxRevenue) * 100)}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function SummaryCard({ icon, label, value, highlight }) {
  return (
    <div
      className={`p-5 wobble-a border-2 shadow-sm ${
        highlight
          ? "bg-[#6E1E3A] text-[#FBE6D8] border-[#6E1E3A]"
          : "bg-white border-[#6E1E3A]/15"
      }`}
    >
      <div className="flex items-center gap-2 font-label tracking-[0.25em] text-[10px] uppercase opacity-85">
        {icon} {label}
      </div>
      <div className={`mt-2 font-display text-3xl md:text-4xl leading-none ${highlight ? "" : "text-[var(--dahlias-forest)]"}`}>
        {value}
      </div>
    </div>
  );
}

function monthLabel(ym) {
  // ym = "YYYY-MM"
  if (!ym || ym.length < 7) return ym || "";
  const [y, m] = ym.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[parseInt(m, 10) - 1] || m} ${y}`;
}
